import { useMemo } from "react";
import { EmptyState } from "@/components/ui/States";
import { type HeatScale, type MapCell, type MapSection } from "@/lib/api/map";
import { qty } from "@/lib/format";
import { describeAddress } from "@/lib/api/address";
import { cellTone, type ColorMode, type HeatIndex } from "./tone";

/**
 * Bo'lak chizmasi.
 *
 * Panjara reyestrdagi kataklardan emas, BO'LAK O'LCHAMIDAN
 * quriladi (`max_row` × `max_col`). Shuning uchun reyestrda
 * yo'q o'rin chizmada TESHIK bo'lib ko'rinadi: raqamlar
 * ketma-ketligidagi uzilish reyestrdagi kamchilikni bildiradi
 * va uni jimgina yopish "u yerda joy bor" degan ma'noni berardi.
 */
export function MapPlan({
  section,
  cells,
  loading,
  truncated,
  cellSize,
  selected,
  onSelect,
  mode,
  heat,
}: {
  section?: MapSection;
  cells: MapCell[];
  loading: boolean;
  truncated: boolean;
  cellSize: number;
  selected: MapCell | null;
  onSelect: (cell: MapCell) => void;
  /** Rang nimani bildiradi: holat, ABC yoki harakat. */
  mode: ColorMode;
  heat?: { index: HeatIndex; scale: HeatScale };
}) {
  const plan = useMemo(() => buildPlan(section, cells), [section, cells]);

  if (loading) {
    return (
      <div className="map-canvas">
        <div className="skeleton" style={{ height: 220 }} />
      </div>
    );
  }

  if (!section || plan.rows.length === 0) {
    return (
      <EmptyState
        icon="map"
        title="Chizma bo'sh"
        hint="Bu bo'lakda yacheyka yo'q yoki bo'lim hali tanlanmagan."
      />
    );
  }

  return (
    <>
      {/* O'q nomlari: raqamlarning o'zi qaysi o'lchov ekanini
          aytmaydi va xodim ularni kod bilan solishtirib
          taxmin qilishga majbur bo'lardi. */}
      {plan.showColumnLabels && (
        <div className="map-axis">
          <span>↓ Stellaj (ikki ustun orasi)</span>
          <span>→ Qavatdagi yacheyka</span>
        </div>
      )}
      <div className="map-canvas">
        <div
          className="map-grid"
          style={{
            gridTemplateColumns: `28px repeat(${plan.cols}, ${cellSize}px)`,
            ["--cell-size" as string]: `${cellSize}px`,
          }}
        >
          {/* Ustun sarlavhalari — faqat panjarali chizmada: chiziqli
              ro'yxatda ustun raqami ma'noga ega emas. */}
          {plan.showColumnLabels && (
            <>
              <div />
              {Array.from({ length: plan.cols }, (_, index) => (
                <div className="map-col-label" key={`col-${index}`}>
                  {/* Har bir raqamni yozish mayda chizmada
                      o'qilmaydigan devorga aylanardi. */}
                  {cellSize >= 18 || (index + 1) % 5 === 0 ? index + 1 : ""}
                </div>
              ))}
            </>
          )}

          {plan.rows.map((row) => (
            <RowCells
              key={row.label}
              row={row}
              cols={plan.cols}
              selected={selected}
              onSelect={onSelect}
              mode={mode}
              heat={heat}
            />
          ))}
        </div>
      </div>

      {truncated && (
        <p className="field-hint">
          Ro'yxat kesilgan: bo'lakdagi barcha katak ko'rsatilmadi. Kichikroq bo'lim tanlang.
        </p>
      )}
    </>
  );
}

function RowCells({
  row,
  cols,
  selected,
  onSelect,
  mode,
  heat,
}: {
  row: PlanRow;
  cols: number;
  selected: MapCell | null;
  onSelect: (cell: MapCell) => void;
  mode: ColorMode;
  heat?: { index: HeatIndex; scale: HeatScale };
}) {
  return (
    <>
      <div className="map-row-label">{row.label}</div>
      {Array.from({ length: cols }, (_, index) => {
        const cell = row.cells[index];
        if (!cell) {
          return (
            <div
              className="map-cell-gap"
              key={`gap-${row.label}-${index}`}
              title="Reyestrda bu o'rin yo'q"
            />
          );
        }
        const tone = cellTone(cell, mode, heat);
        return (
          <button
            type="button"
            key={cell.location_id}
            className={`map-cell ${tone.key}${tone.mismatch ? " tone-mismatch" : ""}${
              selected?.location_id === cell.location_id ? " selected" : ""
            }`}
            onClick={() => onSelect(cell)}
            title={`${describeAddress(cell.code)}\n${tone.label} · qoldiq ${qty(cell.on_hand_qty)} · ${cell.distinct_sku_count} SKU`}
            aria-label={`${cell.code}, ${tone.label}`}
          />
        );
      })}
    </>
  );
}

interface PlanRow {
  label: string;
  /** Ustun bo'yicha kataklar; bo'sh joy — reyestrda yo'q o'rin. */
  cells: Array<MapCell | undefined>;
}

interface Plan {
  rows: PlanRow[];
  cols: number;
  showColumnLabels: boolean;
}

/** Chiziqli ro'yxat necha ustunga o'ralishi. */
const FLOOR_ROW_WIDTH = 25;

function buildPlan(section: MapSection | undefined, cells: MapCell[]): Plan {
  if (!section || cells.length === 0) return { rows: [], cols: 0, showColumnLabels: false };

  // Stellajli korpus: satr — stellaj, ustun — yacheyka. Bu
  // omborning HAQIQIY tuzilishi (TZ §5.1), shuning uchun chizma
  // ham shunday.
  if (section.kind === "RACK") {
    const cols = Math.max(section.max_col, 1);
    const rows: PlanRow[] = [];
    for (let rowIndex = 1; rowIndex <= Math.max(section.max_row, 1); rowIndex += 1) {
      const rowCells: Array<MapCell | undefined> = new Array(cols).fill(undefined);
      for (const cell of cells) {
        if (cell.row === rowIndex && cell.col != null && cell.col >= 1 && cell.col <= cols) {
          rowCells[cell.col - 1] = cell;
        }
      }
      rows.push({ label: String(rowIndex).padStart(2, "0"), cells: rowCells });
    }
    return { rows, cols, showColumnLabels: true };
  }

  // Chiziqli ro'yxat (B/C korpus, operatsion zonalar): tartib
  // raqami bo'yicha o'raladi. Ustun raqami bu yerda jismoniy
  // ma'noga EGA EMAS, shuning uchun sarlavhasi ham yo'q.
  const sorted = [...cells].sort((a, b) => (a.row ?? 0) - (b.row ?? 0) || a.code.localeCompare(b.code));
  const rows: PlanRow[] = [];
  for (let index = 0; index < sorted.length; index += FLOOR_ROW_WIDTH) {
    const slice = sorted.slice(index, index + FLOOR_ROW_WIDTH);
    rows.push({
      label: slice[0]?.code.replace(/[0-9]+$/, "") + String(index + 1),
      cells: slice,
    });
  }
  return { rows, cols: FLOOR_ROW_WIDTH, showColumnLabels: false };
}
